import { ChevronRight, Cube } from "@procore/core-icons";
import { useMemo } from "react";

import type { ListNode, Node, Tree } from "components/objectTree/types";
import { useHidden } from "contexts/hidden";
import type { ModelTreeNodeData } from "features/modelTree/types";
import { isContainerFileNode } from "utils/objectPath";
import { getGroupStateForIdSet } from "utils/treeHelpers";

import { ITEM_HEIGHT_IOS } from "../constants";
import { invertGroupState, useIsolationVisibility } from "../hooks/useIsolationVisibility";
import { formatNodeDisplayName } from "../utils/formatting";
import { VisibilityToggleButton } from "./VisibilityToggleButton";

export type MobileObjectTreeRowProps = {
    node: Node<ModelTreeNodeData>;
    tree: Tree<ModelTreeNodeData>;
    onDrillInto: (path: string) => void;
    onLeafTap: (node: Node<ModelTreeNodeData>) => void;
};

export function MobileObjectTreeRow({ node, tree, onDrillInto, onLeafTap }: MobileObjectTreeRowProps) {
    const hidden = useHidden();
    const isolation = useIsolationVisibility();
    const isolationVisibleIds = isolation.visibleIds;

    const item = useMemo((): ListNode<ModelTreeNodeData> => ({ node, parent: null, level: 0 }), [node]);
    const hiddenState = useMemo(() => {
        if (isolationVisibleIds) {
            return invertGroupState(getGroupStateForIdSet(isolationVisibleIds, item, tree));
        }
        return getGroupStateForIdSet(hidden.ids, item, tree);
    }, [hidden.ids, isolationVisibleIds, item, tree]);
    const isHidden =
        hiddenState === "all" || (!isolationVisibleIds && !node.isLeaf && hidden.ids.has(node.data.object.id));

    const handleClick = (e: React.MouseEvent) => {
        e.stopPropagation();
        if (node.isLeaf) {
            onLeafTap(node);
        } else {
            onDrillInto(node.path);
        }
    };

    const displayName = formatNodeDisplayName(item);
    const contentOpacity = isHidden ? 0.4 : 1;

    return (
        <div
            onClick={handleClick}
            style={{
                display: "flex",
                alignItems: "center",
                minHeight: ITEM_HEIGHT_IOS,
                padding: "0 16px",
                gap: 10,
                borderBottom: "1px solid rgba(172, 181, 185, 0.32)",
                userSelect: "none",
            }}
        >
            {isContainerFileNode(node.path) && (
                <Cube style={{ width: 20, height: 20, flexShrink: 0, color: "#232729", opacity: contentOpacity }} />
            )}
            <span
                style={{
                    flex: 1,
                    minWidth: 0,
                    display: "-webkit-box",
                    WebkitBoxOrient: "vertical",
                    WebkitLineClamp: 2,
                    overflow: "hidden",
                    overflowWrap: "anywhere",
                    fontSize: 18,
                    fontWeight: 600,
                    color: "#171a1c",
                    letterSpacing: "-0.28px",
                    lineHeight: "20px",
                    opacity: contentOpacity,
                }}
            >
                {displayName}
            </span>
            <VisibilityToggleButton node={node} tree={tree} />
            {!node.isLeaf && <ChevronRight style={{ flexShrink: 0, width: 20, height: 20, color: "#ACB5B9" }} />}
        </div>
    );
}
